import { useEffect, useState, type ChangeEvent } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { announceChange } from '../../ui/changes.ts';
import { ErrorBox } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { ImportKind, ImportPreview, ImportResult } from './types.ts';
import { MarketPicker, downloadText } from './ui.tsx';

const STATUS_LABELS: Record<ImportPreview['rows'][number]['status'], string> = {
  new: 'New',
  duplicate: 'Duplicate',
  exists: 'Exists',
  error: 'Error',
};

/**
 * CSV import in one pass: pick a file → check the column mapping and the row report → import.
 * The preview is the only confirmation; nothing changes before "Import".
 */
export function Import() {
  const { query, setQuery } = useRouter();
  const toast = useToast();
  const kind = (query.get('kind') as ImportKind | null) ?? 'coupons';
  const marketParam = query.get('market') ?? '';
  const sitesOp = useOp<{ market: string; sites: { ref: string; name: string }[] }>('admin.merchants.list', { ...(marketParam ? { market: marketParam } : {}), limit: 1 });
  const market = marketParam || sitesOp.data?.market || '';
  const sites = sitesOp.data?.sites ?? [];

  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [mapping, setMapping] = useState<Record<string, string> | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [checking, setChecking] = useState(false);
  const [targets, setTargets] = useState<string[]>([]);
  const [defaultState, setDefaultState] = useState<'pending_review' | 'approved'>('pending_review');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => setTargets(sites.map((s) => s.ref)), [sitesOp.data]);
  useEffect(() => {
    setMapping(null);
    setPreview(null);
    setResult(null);
  }, [kind, market]);

  useEffect(() => {
    if (!csv || !market) return;
    let cancelled = false;
    setChecking(true);
    const timer = window.setTimeout(() => {
      api
        .op<ImportPreview>('admin.import.preview', { kind, market, csv, defaultState, ...(mapping ? { mapping } : {}) })
        .then((data) => {
          if (cancelled) return;
          setPreview(data);
          setError(null);
          if (!mapping) setMapping(Object.fromEntries(data.columns.map((c) => [c.name, c.field])));
        })
        .catch((err: unknown) => !cancelled && setError(err))
        .finally(() => !cancelled && setChecking(false));
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [csv, kind, market, mapping, defaultState]);

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setMapping(null);
    setResult(null);
    setCsv(await file.text());
  };

  const runImport = async () => {
    if (!preview || !mapping) return;
    setImporting(true);
    try {
      const done = await api.op<ImportResult>('admin.import.commit', { kind, market, csv, mapping, defaultState, offerTo: targets, confirm: true });
      setResult(done);
      toast('success', `Imported ${done.created} ${kind}${done.approved ? `, ${done.approved} live on sites` : ''}`);
      announceChange();
      setCsv('');
      setPreview(null);
      setFileName('');
    } catch (err) {
      toast('error', describeError(err));
    } finally {
      setImporting(false);
    }
  };

  const exportCsv = async (what: ImportKind) => {
    try {
      const file = await api.op<{ filename: string; csv: string; rows: number }>('admin.export.csv', { kind: what, market });
      downloadText(file.filename, file.csv);
      toast('success', `Exported ${file.rows} ${what}`);
    } catch (err) {
      toast('error', describeError(err));
    }
  };

  const importable = preview ? preview.totals.new : 0;
  return (
    <section className="page narrow">
      <header className="page-head">
        <div>
          <h1>Import and export</h1>
          <p className="muted">CSV with a header row; comma or semicolon. Dates as YYYY-MM-DD or DD/MM/YYYY.</p>
        </div>
      </header>
      <div className="card">
        <div className="toolbar">
          <div className="segmented" role="group" aria-label="What to import">
            {(['coupons', 'merchants'] as const).map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => setQuery({ kind: k === 'coupons' ? null : k }, { replace: true })}>
                {k === 'coupons' ? 'Coupons' : 'Merchants'}
              </button>
            ))}
          </div>
          <MarketPicker value={market} onChange={(code) => setQuery({ market: code || null }, { replace: true })} />
          <label className="btn file-button">
            {fileName ? `File: ${fileName}` : 'Choose CSV file'}
            <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="visually-hidden" data-testid="csv-file" />
          </label>
          {checking ? <span className="muted">Checking…</span> : null}
        </div>
        {!csv ? (
          <details>
            <summary className="muted">…or paste CSV text</summary>
            <textarea rows={5} aria-label="CSV text" placeholder={kind === 'coupons' ? 'merchant,code,title,discount,ends_at,state\nnike,SAVE10,10% off,10%,2026-12-31,live' : 'name,homepage,categories\nBoots,https://www.boots.com,beauty'} onBlur={(e) => e.target.value.trim() && setCsv(e.target.value)} />
          </details>
        ) : null}
        {error ? <ErrorBox error={error} /> : null}
      </div>

      {preview && mapping ? (
        <>
          <div className="card">
            <h2>Columns</h2>
            <p className="muted small">Each column of your file, an example value and the field it fills. Unmapped columns are ignored.</p>
            <ul className="mapping">
              {preview.columns.map((column) => (
                <li key={column.name}>
                  <div className="mapping-source">
                    <strong>{column.name}</strong>
                    <small className="muted truncate">{column.sample || 'empty'}</small>
                  </div>
                  <span aria-hidden="true">→</span>
                  <select aria-label={`Field for ${column.name}`} value={mapping[column.name] ?? ''} onChange={(e) => setMapping({ ...mapping, [column.name]: e.target.value })}>
                    <option value="">Ignore</option>
                    {preview.fields.map((field) => (
                      <option key={field.key} value={field.key} disabled={Object.entries(mapping).some(([col, f]) => f === field.key && col !== column.name)}>
                        {field.label}
                        {field.required ? ' *' : ''}
                        {field.hint ? ` (${field.hint})` : ''}
                      </option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
            {preview.missingRequired.length ? <p className="field-error">Map these required fields: {preview.missingRequired.join(', ')}</p> : null}
          </div>

          <div className="card">
            <h2>Rows</h2>
            <ul className="summary-chips">
              <li>{preview.totals.rows} rows</li>
              <li>{preview.totals.new} new</li>
              {preview.totals.duplicate ? <li className="warn">{preview.totals.duplicate} duplicates (skipped)</li> : null}
              {preview.totals.exists ? <li className="warn">{preview.totals.exists} already exist (skipped)</li> : null}
              {preview.totals.error ? <li className="warn">{preview.totals.error} with errors (skipped)</li> : null}
              {kind === 'coupons' && preview.totals.goLive ? <li>{preview.totals.goLive} go live</li> : null}
            </ul>
            {preview.rows.some((r) => r.status !== 'new') ? (
              <table className="table compact">
                <thead>
                  <tr>
                    <th scope="col" className="num">
                      Line
                    </th>
                    <th scope="col">Row</th>
                    <th scope="col">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.line}>
                      <td className="num">{row.line}</td>
                      <td className="truncate">{row.summary}</td>
                      <td>
                        <span className={`badge ${row.status === 'new' ? 'badge-ok' : row.status === 'error' ? 'badge-warn' : 'badge-muted'}`}>{STATUS_LABELS[row.status]}</span> {row.errors.join('; ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {preview.truncated ? <p className="muted small">Showing the first rows; all rows are checked again on import.</p> : null}
          </div>

          <div className="card">
            <h2>{kind === 'coupons' ? 'Offer to sites' : 'Show on sites'}</h2>
            <div className="radio-row">
              {sites.map((site) => (
                <label key={site.ref} className="inline-check">
                  <input type="checkbox" checked={targets.includes(site.ref)} onChange={(e) => setTargets(e.target.checked ? [...targets, site.ref] : targets.filter((r) => r !== site.ref))} />
                  {site.name}
                </label>
              ))}
            </div>
            {kind === 'coupons' ? (
              <fieldset className="import-state">
                <legend>State for rows without a state column value</legend>
                <div className="radio-row">
                  <label className="inline-check">
                    <input type="radio" name="default-state" checked={defaultState === 'pending_review'} onChange={() => setDefaultState('pending_review')} />
                    In review
                  </label>
                  <label className="inline-check">
                    <input type="radio" name="default-state" checked={defaultState === 'approved'} onChange={() => setDefaultState('approved')} />
                    Approved (live on the chosen sites)
                  </label>
                </div>
              </fieldset>
            ) : null}
            <div className="form-actions">
              <button type="button" className="btn btn-primary" disabled={importing || checking || importable === 0 || preview.missingRequired.length > 0} onClick={runImport}>
                {importing ? 'Importing…' : `Import ${importable} ${kind}`}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {result ? (
        <div className="card" role="status">
          <h2>Imported</h2>
          <p>
            {result.created} {result.kind} added in {result.market}
            {result.approved ? `, ${result.approved} site listings approved` : ''} · {result.skipped} skipped · {result.errors} with errors.
          </p>
          <p>
            {result.kind === 'coupons' ? (
              <Link to="/review">Open the review queue</Link>
            ) : (
              <Link to="/merchants">Open merchants</Link>
            )}
          </p>
        </div>
      ) : null}

      <div className="card">
        <h2>Export</h2>
        <p className="muted small">Same columns as the import, so an export can be edited and imported again (existing rows are skipped). Coupon exports contain codes.</p>
        <div className="form-actions">
          <button type="button" className="btn" disabled={!market} onClick={() => exportCsv('merchants')}>
            Export merchants ({market})
          </button>
          <button type="button" className="btn" disabled={!market} onClick={() => exportCsv('coupons')}>
            Export current coupons ({market})
          </button>
        </div>
      </div>
    </section>
  );
}
